// frontend/src/components/CookieConsentBanner.tsx
//
// First-visit consent bar + a "manage preferences" dialog (re-openable from the
// footer). Gates analytics (self-hosted beacon + Vercel <Analytics/>). Essential
// cookies (auth) are always on and not gated. Default = nothing tracked until
// the user chooses. Themed to the Percoro palette.
import React from 'react';
import {
  Box, Button, Typography, Link, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Switch, Stack, Divider,
} from '@mui/material';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const TEAL = '#00B4A0';
const BTN = '#2D6E6A';
const BTN_HOVER = '#1F4E4A';

const CookieConsentBanner: React.FC = () => {
  const {
    hasDecided, analyticsAllowed, acceptAll, rejectNonEssential,
    setAnalytics, manageOpen, openManage, closeManage,
  } = useCookieConsent();

  // Local toggle state for the manage dialog (seeded from current consent).
  const [analyticsToggle, setAnalyticsToggle] = React.useState(analyticsAllowed);
  React.useEffect(() => { setAnalyticsToggle(analyticsAllowed); }, [analyticsAllowed, manageOpen]);

  const showBar = !hasDecided && !manageOpen;

  return (
    <>
      {/* First-visit bottom bar */}
      {showBar && (
        <Box
          role="region"
          aria-label="Cookie consent"
          sx={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1400,
            bgcolor: '#fff', borderTop: `3px solid ${TEAL}`,
            boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
            px: { xs: 2, md: 4 }, py: { xs: 2, md: 2.5 },
            display: 'flex', flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' }, gap: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.55, flex: 1 }}>
            We use essential cookies to run Percoro, and optional analytics to understand how the
            site is used. You can accept or reject analytics. See our{' '}
            <Link href="/cookie" sx={{ color: BTN, fontWeight: 600 }}>Cookie Policy</Link>.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ flexShrink: 0 }}>
            <Button onClick={openManage} sx={{ color: BTN, textTransform: 'none', fontWeight: 600 }}>
              Manage
            </Button>
            <Button
              onClick={rejectNonEssential}
              variant="outlined"
              sx={{ color: BTN, borderColor: BTN, textTransform: 'none', fontWeight: 600,
                '&:hover': { borderColor: BTN_HOVER, bgcolor: 'rgba(45,110,106,0.04)' } }}
            >
              Reject non-essential
            </Button>
            <Button
              onClick={acceptAll}
              variant="contained"
              sx={{ bgcolor: BTN, textTransform: 'none', fontWeight: 600,
                '&:hover': { bgcolor: BTN_HOVER } }}
            >
              Accept all
            </Button>
          </Stack>
        </Box>
      )}

      {/* Manage preferences dialog */}
      <Dialog open={manageOpen} onClose={closeManage} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cookie preferences</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.88rem', color: '#555', mb: 2 }}>
            Manage how Percoro uses cookies and similar technologies. Essential cookies are required
            for the site to function and can't be turned off. See our{' '}
            <Link href="/cookie" sx={{ color: BTN, fontWeight: 600 }}>Cookie Policy</Link> for details.
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ py: 1 }}>
            <FormControlLabel
              control={<Switch checked disabled />}
              label={<Typography sx={{ fontWeight: 600 }}>Essential <Typography component="span" sx={{ fontWeight: 400, color: '#777', fontSize: '0.85rem' }}>(always on)</Typography></Typography>}
            />
            <Typography sx={{ fontSize: '0.82rem', color: '#777', ml: 6, mt: -0.5 }}>
              Authentication and security. Required to sign in and use the app.
            </Typography>
          </Box>

          <Box sx={{ py: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={analyticsToggle}
                  onChange={(e) => setAnalyticsToggle(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TEAL } }}
                />
              }
              label={<Typography sx={{ fontWeight: 600 }}>Analytics</Typography>}
            />
            <Typography sx={{ fontSize: '0.82rem', color: '#777', ml: 6, mt: -0.5 }}>
              Anonymous, privacy-friendly usage measurement (page views and product events) to help
              us improve Percoro. Off by default.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeManage} sx={{ color: '#777', textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={() => { setAnalytics(analyticsToggle); closeManage(); }}
            variant="contained"
            sx={{ bgcolor: BTN, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: BTN_HOVER } }}
          >
            Save preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
